import { FC, useState, useMemo } from "react"
import { AbcItem } from "../../../app/reducers/analyticsSlice"
import { fmtRub, fmtPct } from "../utils/formatters"
import { Button } from "@radix-ui/themes"
import { DataTable, type DataTableColumn } from "../../DataTable/DataTable"

type AbcSectionProps = { items: AbcItem[] }

type AbcRow = AbcItem & { _index: number }

const columns: DataTableColumn<AbcRow>[] = [
  { key: "_index", header: "#", cell: (row) => row._index },
  {
    key: "grade",
    header: "Группа",
    cell: (item) => (
      <span className={`grade-badge grade-${item.grade}`}>{item.grade}</span>
    ),
  },
  {
    key: "article",
    header: "Артикул",
    cell: (item) =>
      item.article.length > 35 ? item.article.slice(0, 35) + "…" : item.article,
  },
  { key: "name", header: "Категория" },
  { key: "revenue", header: "Выручка", cell: (item) => fmtRub(item.revenue) },
  { key: "payout", header: "К выплате", cell: (item) => fmtRub(item.payout) },
  {
    key: "cost",
    header: "Себест.",
    cell: (item) => (item.cost > 0 ? fmtRub(item.cost) : "—"),
    cellStyle: (item) => ({
      color: item.cost > 0 ? "inherit" : "var(--gray-7)",
    }),
  },
  {
    key: "profit",
    header: "Прибыль",
    cell: (item) => (item.cost > 0 ? fmtRub(item.profit) : "—"),
    cellStyle: (item) => ({
      color:
        item.cost > 0
          ? item.profit >= 0
            ? "#1a7f44"
            : "#c92a2a"
          : "var(--gray-7)",
      fontWeight: item.cost > 0 ? 600 : 400,
    }),
  },
  {
    key: "margin",
    header: "Маржа",
    cell: (item) => (item.cost > 0 ? fmtPct(item.margin) : "—"),
    cellStyle: (item) => ({
      color:
        item.cost > 0
          ? item.margin > 20
            ? "#1a7f44"
            : item.margin > 0
            ? "#865900"
            : "#c92a2a"
          : "var(--gray-7)",
    }),
  },
  { key: "salesCount", header: "Продажи" },
  {
    key: "revenueShare",
    header: "Доля %",
    cell: (item) => `${item.revenueShare}%`,
  },
  {
    key: "cumulativeShare",
    header: "Накопит. %",
    cell: (item) => `${item.cumulativeShare}%`,
  },
]

export const AbcSection: FC<AbcSectionProps> = ({ items }) => {
  const [filter, setFilter] = useState<"all" | "A" | "B" | "C">("all")

  const filtered =
    filter === "all" ? items : items.filter((i) => i.grade === filter)

  const counts = useMemo(
    () => ({
      A: items.filter((i) => i.grade === "A").length,
      B: items.filter((i) => i.grade === "B").length,
      C: items.filter((i) => i.grade === "C").length,
    }),
    [items],
  )

  return (
    <div className="chart-wrap">
      <div className="chart-title">ABC-анализ товаров</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {(["all", "A", "B", "C"] as const).map((g) => (
          <Button asChild key={g} variant="ghost">
            <button
              className={`analytics-tab ${filter === g ? "active" : ""}`}
              style={{
                borderBottom: "none",
                padding: "4px 14px",
                fontSize: 13,
              }}
              onClick={() => setFilter(g)}
            >
              {g === "all" ? `Все (${items.length})` : `${g} (${counts[g]})`}
            </button>
          </Button>
        ))}
      </div>
      <div className="analytics-table-wrap">
        <DataTable
          columns={columns}
          data={filtered.map((item, i) => ({ ...item, _index: i + 1 }))}
          rowKey={(item) => item.nmId}
          variant="ghost"
          size="2"
        />
      </div>
    </div>
  )
}
